import { AuthMethod } from './auth-method';
import { CredentialsObject } from './credentials-object';
import { AccountView } from '../account-view';
import { LinkAuthMethodService } from '../../domains/services/LinkAuthMethodService';
import { LinkAuthMethod } from '../../domain-adapters/adapters/LinkAuthMethod';
import { loadAccountWindow } from '../account-window-loader';
import { eventWebGuestHasSwitchedGameProgress } from '../../general/modules/appEvents';
import { LinkAuthMethodError } from '../../domains/valueObject/Errors/LinkAuthMethodError';
import { Localization } from '../../localization/localization';
import { AuthMethodAlreadyLinkedError } from '../../domains/valueObject/Errors/AuthMethodAlreadyLinkedError';
import { Header } from '../../layout/header';
import { GameClient } from '../../gameClient/gameClient';
import {
    FinishAccountLinkingReward,
    LinkAuthMethodErrorTypeEnum
} from '../../gameClient/clientCommands/FinishAccountLinkingReward';

function handleLinkError(linkAuthMethodResult: Error, authMethod: AuthMethod) {
    if (linkAuthMethodResult instanceof LinkAuthMethodError) {
        console.error('Auth method is not linked', linkAuthMethodResult);
        window.Sentry.captureException(linkAuthMethodResult.message);
        AccountView.hideAccountLoadingState();
        AccountView.showLinkError(linkAuthMethodResult.message, authMethod);
        GameClient.getInstance().sendCommand<void>(new FinishAccountLinkingReward({
            isSuccess: false,
            linkingError: LinkAuthMethodErrorTypeEnum.FAILED_TO_LINK
        }));
        return;
    }

    if (linkAuthMethodResult instanceof AuthMethodAlreadyLinkedError) {
        console.error('Auth method is already linked. Canceled.', linkAuthMethodResult);
        AccountView.hideAccountLoadingState();
        AccountView.showLogoutBtnForWebGuest();
        AccountView.showLinkError(linkAuthMethodResult.message, authMethod);
        GameClient.getInstance().sendCommand<void>(new FinishAccountLinkingReward({
            isSuccess: false,
            linkingError: LinkAuthMethodErrorTypeEnum.AUTH_METHOD_ALREADY_LINKED
        }));
        return;
    }

    AccountView.hideAccountLoadingState();
    console.error('Unexpected unsuccessful result', linkAuthMethodResult);
    window.Sentry.captureException(new Error('Unexpected unsuccessful link result'));
    AccountView.showLinkError(Localization._n('common_error'), authMethod);
    GameClient.getInstance().sendCommand<void>(new FinishAccountLinkingReward({
        isSuccess: false,
        linkingError: LinkAuthMethodErrorTypeEnum.UNKNOWN_ERROR
    }));
}

function handleLinkSuccess() {
    eventWebGuestHasSwitchedGameProgress();
    AccountView.hideAccountLoadingState();
    AccountView.hideGuestWarningIcon();
    Header.showLogoutButton();
    loadAccountWindow();
    GameClient.getInstance().sendCommand<void>(new FinishAccountLinkingReward({isSuccess: true}));
}

export async function executeLinkAuthMethod(
    authMethod: AuthMethod,
    credentials: CredentialsObject,
    isBigReward?: boolean
): Promise<boolean> {
    try {
        AccountView.hideAccountError();
        AccountView.hideLinkError();
        AccountView.showAccountLoadingState();

        const linkAuthMethodService = new LinkAuthMethodService(new LinkAuthMethod());
        const linkAuthMethodResult = await linkAuthMethodService.linkAuthMethod(
            authMethod,
            credentials,
            isBigReward
        );

        if (linkAuthMethodResult === true) {
            handleLinkSuccess();
            return true;
        }

        handleLinkError(linkAuthMethodResult, authMethod);
    } catch (exception) {
        console.error('Got exception', exception);
        window.Sentry.captureException(`message: ${exception.message} stack: ${exception.stack}`);
        handleLinkError(exception, authMethod);
    }

    return false;
}
